import * as React from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

import { DateRangePicker, DollarFormatter, Page, PercentageFormatter } from '@src/components';
import { fetchAdSummaryByAd, fetchAdPreview } from '@src/api';
import api from '@src/api/clients';
import { useAuth } from '@src/context';
import { formatDateToYMD } from '@src/utils/dateUtils';

import {
  CLIENT_FIELDS,
  MAX_CLIENT_META_PAGES,
  arraysEqual,
  computeDerivedMetrics,
  getLastThirtyDays,
  safeDiv,
  safeNumber,
} from '../creative-performance/utils';

const VIEW_STORAGE_KEY = 'adPerformance.viewMode';
const METRICS_STORAGE_KEY = 'adPerformance.selectedMetrics';

const METRIC_OPTIONS = [
  { key: 'spend', label: 'Spend' },
  { key: 'leadsCpl', label: 'Leads + CPL' },
  { key: 'bookedCpb', label: 'Booked + Cost/Booked' },
  { key: 'issuesCpi', label: 'Issues + Cost/Issue' },
  { key: 'demosCpd', label: 'Demos + Cost/Demo' },
  { key: 'soldCps', label: 'Sold + Cost/Sale' },
  { key: 'revenueCom', label: 'Revenue + CoM' },
];

const DEFAULT_SELECTED_METRICS = ['spend', 'leadsCpl', 'bookedCpb'];

const PAGE_SIZE = 50;

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active only' },
  { value: 'all', label: 'Active + Paused' },
];

async function fetchAllClientMetadata() {
  const allClients = [];
  let page = 1;
  while (page <= MAX_CLIENT_META_PAGES) {
    const response = await api.request({
      url: 'dashboard/clients/',
      method: 'GET',
      payload: { page, page_size: 250, ordering: 'name', fields: CLIENT_FIELDS },
    });
    const data = response?.data;
    if (Array.isArray(data)) { allClients.push(...data); break; }
    const results = Array.isArray(data?.results) ? data.results : [];
    allClients.push(...results);
    if (!data?.next) break;
    page += 1;
  }
  return allClients;
}

const mapRow = (row = {}, clientMetaById) => {
  const clientId = row.client_id ? String(row.client_id) : '';
  const clientMeta = clientId ? clientMetaById.get(clientId) : null;
  const base = {
    uuid: row.uuid || `${row.ad_id || row.ad_name || ''}-${clientId}`,
    clientId,
    clientName: clientMeta?.name || row.client_name || row.client__name || '',
    adId: row.ad_id || '',
    adName: row.ad_name || row.ad || '',
    adSetName: row.ad_set_name || '',
    campaign: row.campaign || '',
    isActive: row.active == null ? true : Boolean(row.active),
    headline: row.headline || '',
    primaryText: row.primary_text || '',
    description: row.description || '',
    destinationUrl: row.destination_url || '',
    ctaType: row.cta_type || '',
    thumbnailUrl: row.thumbnail_url || '',
    imageUrl: row.image_url || '',
    videoId: row.video_id || '',
    facebookFormat: row.facebook_format || '',
    spend: safeNumber(row.total_spend_sum),
    impressions: safeNumber(row.impressions_sum),
    clicks: safeNumber(row.clicks_sum),
    landingPageViews: safeNumber(row.landing_page_views_sum),
    videoPlays3s: safeNumber(row.video_plays_3s_sum),
    thruplays: safeNumber(row.thruplays_sum),
    leads: safeNumber(row.total_leads_sum),
    booked: safeNumber(row.apps_booked_sum || row.booked_sum),
    issues: safeNumber(row.issue_sum || row.issues_sum),
    demos: safeNumber(row.demo_sum || row.demos_sum),
    netSold: safeNumber(row.net_sold_sum || row.net_sales_sum),
    netAmount: safeNumber(row.net_total_sum || row.net_amount_sum),
  };
  const derived = computeDerivedMetrics(base);
  const cpb = safeDiv(base.spend, base.booked);
  const cpi = safeDiv(base.spend, base.issues);
  const cpd = safeDiv(base.spend, base.demos);
  const cps = safeDiv(base.spend, base.netSold);
  return { ...base, ...derived, costPerBooked: cpb, costPerIssue: cpi, costPerDemo: cpd, costPerSale: cps };
};

function StatusPill({ active }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1,
        py: 0.25,
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        backgroundColor: active ? 'success.light' : 'grey.300',
        color: active ? 'success.dark' : 'text.secondary',
      }}
    >
      {active ? 'Active' : 'Paused'}
    </Box>
  );
}

function MetricCell({ label, value, sub, subLabel }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>{label}</Typography>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{value}</Typography>
      {sub != null ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
          {subLabel}: {sub}
        </Typography>
      ) : null}
    </Box>
  );
}

function renderMetricCells(row, selected) {
  const cells = [];
  if (selected.includes('spend')) {
    cells.push(<MetricCell key="spend" label="Spend" value={<DollarFormatter value={row.spend} />} />);
  }
  if (selected.includes('leadsCpl')) {
    cells.push(
      <MetricCell
        key="leadsCpl"
        label="Leads"
        value={row.leads.toLocaleString()}
        sub={row.cpl > 0 ? <DollarFormatter value={row.cpl} /> : '—'}
        subLabel="CPL"
      />
    );
  }
  if (selected.includes('bookedCpb')) {
    cells.push(
      <MetricCell
        key="bookedCpb"
        label="Booked"
        value={row.booked.toLocaleString()}
        sub={row.costPerBooked > 0 ? <DollarFormatter value={row.costPerBooked} /> : '—'}
        subLabel="Cost/Booked"
      />
    );
  }
  if (selected.includes('issuesCpi')) {
    cells.push(
      <MetricCell
        key="issuesCpi"
        label="Issues"
        value={row.issues.toLocaleString()}
        sub={row.costPerIssue > 0 ? <DollarFormatter value={row.costPerIssue} /> : '—'}
        subLabel="Cost/Issue"
      />
    );
  }
  if (selected.includes('demosCpd')) {
    cells.push(
      <MetricCell
        key="demosCpd"
        label="Demos"
        value={row.demos.toLocaleString()}
        sub={row.costPerDemo > 0 ? <DollarFormatter value={row.costPerDemo} /> : '—'}
        subLabel="Cost/Demo"
      />
    );
  }
  if (selected.includes('soldCps')) {
    cells.push(
      <MetricCell
        key="soldCps"
        label="Sold"
        value={row.netSold.toLocaleString()}
        sub={row.costPerSale > 0 ? <DollarFormatter value={row.costPerSale} /> : '—'}
        subLabel="Cost/Sale"
      />
    );
  }
  if (selected.includes('revenueCom')) {
    cells.push(
      <MetricCell
        key="revenueCom"
        label="Revenue"
        value={row.netAmount > 0 ? <DollarFormatter value={row.netAmount} /> : '—'}
        sub={row.netAmount > 0 ? <PercentageFormatter value={row.costOfMarketing} /> : '—'}
        subLabel="CoM"
      />
    );
  }
  return cells;
}

const pickMedia = (row) => row.imageUrl || row.thumbnailUrl || '';

const isVideoRow = (row) =>
  row.facebookFormat === 'VIDEO' ||
  row.facebookFormat === 'DYNAMIC' ||
  Boolean(row.videoId);

// Renders the stored creative poster. For the rare ad with no stored
// thumbnail, lazily fetches the live Facebook preview iframe (same source
// as the detail modal) once the tile scrolls into view. The fetch is
// bounded by pagination and only fires for missing-thumbnail tiles, so it
// stays cheap even under concurrent traffic.
function TilePreview({ row }) {
  const media = pickMedia(row);
  const containerRef = React.useRef(null);
  const [inView, setInView] = React.useState(false);
  const [iframeHtml, setIframeHtml] = React.useState('');
  const [state, setState] = React.useState('idle'); // idle | loading | done | error

  React.useEffect(() => {
    if (media || inView) return undefined;
    const node = containerRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [media, inView]);

  React.useEffect(() => {
    if (media || !inView || state !== 'idle' || !row.adId) return undefined;
    let cancelled = false;
    setState('loading');
    fetchAdPreview({ ad_id: row.adId, ad_format: 'MOBILE_FEED_STANDARD' })
      .then((response) => {
        if (cancelled) return;
        const html = response?.data?.iframe_html || '';
        setIframeHtml(html);
        setState(html ? 'done' : 'error');
      })
      .catch(() => { if (!cancelled) setState('error'); });
    return () => { cancelled = true; };
  }, [media, inView, state, row.adId]);

  if (media) {
    return (
      <Box
        component="img"
        src={media}
        alt={row.adName || 'Ad creative'}
        loading="lazy"
        sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
      />
    );
  }

  return (
    <Box ref={containerRef} sx={{ position: 'absolute', inset: 0 }}>
      {state === 'loading' ? (
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={24} />
        </Box>
      ) : state === 'done' ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            overflow: 'hidden',
            '& iframe': { width: '100% !important', height: '100% !important', border: 0, display: 'block' },
          }}
          dangerouslySetInnerHTML={{ __html: iframeHtml }}
        />
      ) : (
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="caption" color="text.secondary">No preview image</Typography>
        </Box>
      )}
    </Box>
  );
}

function AdTile({ row, selectedMetrics, onClick }) {
  const paused = !row.isActive;
  return (
    <Card
      onClick={() => onClick(row)}
      sx={{
        cursor: 'pointer',
        opacity: paused ? 0.65 : 1,
        transition: 'transform 120ms ease, box-shadow 120ms ease',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Box sx={{ position: 'relative', pt: '100%', bgcolor: 'grey.100', borderBottom: '1px solid', borderColor: 'divider' }}>
        <TilePreview row={row} />
        {isVideoRow(row) ? (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                bgcolor: 'rgba(0,0,0,0.55)',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PlayArrowIcon fontSize="large" />
            </Box>
          </Box>
        ) : null}
        <Box sx={{ position: 'absolute', top: 8, left: 8 }}>
          <StatusPill active={!paused} />
        </Box>
      </Box>
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.3, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
            {row.adName || '(no name)'}
          </Typography>
          {row.clientName ? (
            <Typography variant="caption" color="text.secondary">{row.clientName}</Typography>
          ) : null}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', gap: 1.25 }}>
          {renderMetricCells(row, selectedMetrics)}
        </Box>
      </CardContent>
    </Card>
  );
}

function AdTable({ rows, selectedMetrics, onRowClick }) {
  return (
    <TableContainer>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 80 }}>Preview</TableCell>
            <TableCell>Ad</TableCell>
            <TableCell>Client</TableCell>
            <TableCell>Status</TableCell>
            {selectedMetrics.includes('spend') && <TableCell align="right">Spend</TableCell>}
            {selectedMetrics.includes('leadsCpl') && <TableCell align="right">Leads</TableCell>}
            {selectedMetrics.includes('leadsCpl') && <TableCell align="right">CPL</TableCell>}
            {selectedMetrics.includes('bookedCpb') && <TableCell align="right">Booked</TableCell>}
            {selectedMetrics.includes('bookedCpb') && <TableCell align="right">Cost/Booked</TableCell>}
            {selectedMetrics.includes('issuesCpi') && <TableCell align="right">Issues</TableCell>}
            {selectedMetrics.includes('issuesCpi') && <TableCell align="right">Cost/Issue</TableCell>}
            {selectedMetrics.includes('demosCpd') && <TableCell align="right">Demos</TableCell>}
            {selectedMetrics.includes('demosCpd') && <TableCell align="right">Cost/Demo</TableCell>}
            {selectedMetrics.includes('soldCps') && <TableCell align="right">Sold</TableCell>}
            {selectedMetrics.includes('soldCps') && <TableCell align="right">Cost/Sale</TableCell>}
            {selectedMetrics.includes('revenueCom') && <TableCell align="right">Revenue</TableCell>}
            {selectedMetrics.includes('revenueCom') && <TableCell align="right">CoM</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const thumb = pickMedia(row);
            return (
              <TableRow
                key={row.uuid}
                hover
                onClick={() => onRowClick(row)}
                sx={{ cursor: 'pointer', opacity: row.isActive ? 1 : 0.65 }}
              >
                <TableCell>
                  {thumb ? (
                    <Box
                      component="img"
                      src={thumb}
                      alt={row.adName}
                      sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
                    />
                  ) : (
                    <Box sx={{ width: 56, height: 56, borderRadius: 1, border: '1px dashed', borderColor: 'divider' }} />
                  )}
                </TableCell>
                <TableCell sx={{ maxWidth: 320 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {row.adName || '(no name)'}
                  </Typography>
                  {row.headline ? (
                    <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                      {row.headline}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>{row.clientName || '—'}</TableCell>
                <TableCell><StatusPill active={row.isActive} /></TableCell>
                {selectedMetrics.includes('spend') && <TableCell align="right"><DollarFormatter value={row.spend} /></TableCell>}
                {selectedMetrics.includes('leadsCpl') && <TableCell align="right">{row.leads.toLocaleString()}</TableCell>}
                {selectedMetrics.includes('leadsCpl') && <TableCell align="right">{row.cpl > 0 ? <DollarFormatter value={row.cpl} /> : '—'}</TableCell>}
                {selectedMetrics.includes('bookedCpb') && <TableCell align="right">{row.booked.toLocaleString()}</TableCell>}
                {selectedMetrics.includes('bookedCpb') && <TableCell align="right">{row.costPerBooked > 0 ? <DollarFormatter value={row.costPerBooked} /> : '—'}</TableCell>}
                {selectedMetrics.includes('issuesCpi') && <TableCell align="right">{row.issues.toLocaleString()}</TableCell>}
                {selectedMetrics.includes('issuesCpi') && <TableCell align="right">{row.costPerIssue > 0 ? <DollarFormatter value={row.costPerIssue} /> : '—'}</TableCell>}
                {selectedMetrics.includes('demosCpd') && <TableCell align="right">{row.demos.toLocaleString()}</TableCell>}
                {selectedMetrics.includes('demosCpd') && <TableCell align="right">{row.costPerDemo > 0 ? <DollarFormatter value={row.costPerDemo} /> : '—'}</TableCell>}
                {selectedMetrics.includes('soldCps') && <TableCell align="right">{row.netSold.toLocaleString()}</TableCell>}
                {selectedMetrics.includes('soldCps') && <TableCell align="right">{row.costPerSale > 0 ? <DollarFormatter value={row.costPerSale} /> : '—'}</TableCell>}
                {selectedMetrics.includes('revenueCom') && <TableCell align="right">{row.netAmount > 0 ? <DollarFormatter value={row.netAmount} /> : '—'}</TableCell>}
                {selectedMetrics.includes('revenueCom') && <TableCell align="right">{row.netAmount > 0 ? <PercentageFormatter value={row.costOfMarketing} /> : '—'}</TableCell>}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function AdPreviewModal({ row, onClose }) {
  const [iframeHtml, setIframeHtml] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (!row) return undefined;
    let cancelled = false;
    setIframeHtml('');
    setError(null);
    setLoading(true);
    fetchAdPreview({ ad_id: row.adId, ad_format: 'MOBILE_FEED_STANDARD' })
      .then((response) => {
        if (cancelled) return;
        const data = response?.data || {};
        setIframeHtml(data.iframe_html || '');
        if (!data.iframe_html) setError('Facebook returned an empty preview.');
      })
      .catch(() => {
        if (cancelled) return;
        setError('Unable to load Facebook preview.');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [row]);

  if (!row) return null;

  return (
    <Dialog open={Boolean(row)} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {row.adName || 'Ad Preview'}
          </Typography>
          {row.clientName ? (
            <Typography variant="caption" color="text.secondary">{row.clientName}</Typography>
          ) : null}
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="flex-start">
          <Box sx={{ width: { xs: '100%', md: 360 }, flexShrink: 0 }}>
            {loading ? (
              <Box sx={{ minHeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'grey.50' }}>
                <CircularProgress size={32} />
              </Box>
            ) : error ? (
              <Box sx={{ minHeight: 200, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'grey.50' }}>
                <Alert severity="warning">{error}</Alert>
              </Box>
            ) : (
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  overflow: 'hidden',
                  bgcolor: 'grey.50',
                  '& iframe': { width: '100%', minHeight: 600, border: 0, display: 'block' },
                }}
                dangerouslySetInnerHTML={{ __html: iframeHtml }}
              />
            )}
          </Box>
          <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
            <Box>
              <Typography variant="overline" color="text.secondary">Status</Typography>
              <Box sx={{ mt: 0.5 }}><StatusPill active={row.isActive} /></Box>
            </Box>
            {row.headline ? (
              <Box>
                <Typography variant="overline" color="text.secondary">Headline</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{row.headline}</Typography>
              </Box>
            ) : null}
            {row.primaryText ? (
              <Box>
                <Typography variant="overline" color="text.secondary">Primary text</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{row.primaryText}</Typography>
              </Box>
            ) : null}
            {row.description ? (
              <Box>
                <Typography variant="overline" color="text.secondary">Description</Typography>
                <Typography variant="body2">{row.description}</Typography>
              </Box>
            ) : null}
            <Stack direction="row" spacing={2}>
              {row.ctaType ? (
                <Box>
                  <Typography variant="overline" color="text.secondary">CTA</Typography>
                  <Typography variant="body2">{row.ctaType}</Typography>
                </Box>
              ) : null}
              {row.destinationUrl ? (
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="overline" color="text.secondary">Destination</Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{row.destinationUrl}</Typography>
                </Box>
              ) : null}
            </Stack>
            <Box>
              <Typography variant="overline" color="text.secondary">Performance</Typography>
              <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: 'repeat(2, 1fr)', mt: 0.5 }}>
                <MetricCell label="Spend" value={<DollarFormatter value={row.spend} />} />
                <MetricCell label="Leads" value={row.leads.toLocaleString()} sub={row.cpl > 0 ? <DollarFormatter value={row.cpl} /> : '—'} subLabel="CPL" />
                <MetricCell label="Booked" value={row.booked.toLocaleString()} sub={row.costPerBooked > 0 ? <DollarFormatter value={row.costPerBooked} /> : '—'} subLabel="Cost/Booked" />
                <MetricCell label="Issues" value={row.issues.toLocaleString()} sub={row.costPerIssue > 0 ? <DollarFormatter value={row.costPerIssue} /> : '—'} subLabel="Cost/Issue" />
                <MetricCell label="Demos" value={row.demos.toLocaleString()} sub={row.costPerDemo > 0 ? <DollarFormatter value={row.costPerDemo} /> : '—'} subLabel="Cost/Demo" />
                <MetricCell label="Sold" value={row.netSold.toLocaleString()} sub={row.costPerSale > 0 ? <DollarFormatter value={row.costPerSale} /> : '—'} subLabel="Cost/Sale" />
                <MetricCell label="Revenue" value={row.netAmount > 0 ? <DollarFormatter value={row.netAmount} /> : '—'} sub={row.netAmount > 0 ? <PercentageFormatter value={row.costOfMarketing} /> : '—'} subLabel="CoM" />
              </Box>
            </Box>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

function readStoredArray(key, fallback) {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : fallback;
  } catch (err) {
    return fallback;
  }
}

function readStoredString(key, fallback) {
  if (typeof window === 'undefined') return fallback;
  try {
    return window.localStorage.getItem(key) || fallback;
  } catch (err) {
    return fallback;
  }
}

function writeStored(key, value) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value));
  } catch (err) {
    // ignore
  }
}

export default function AdPerformancePage() {
  const { state: authState } = useAuth();
  const user = authState?.user;
  const isStaff = Boolean(user?.is_staff || user?.is_superuser);
  const clientMemberships = Array.isArray(user?.client_memberships) ? user.client_memberships : [];

  const [defaultStart, defaultEnd] = React.useMemo(() => getLastThirtyDays(), []);
  const [startDate, setStartDate] = React.useState(defaultStart);
  const [endDate, setEndDate] = React.useState(defaultEnd);
  const [loading, setLoading] = React.useState(true);
  const [loadingClientMeta, setLoadingClientMeta] = React.useState(isStaff);
  const [error, setError] = React.useState(null);
  const [rows, setRows] = React.useState([]);
  const [clientMetadata, setClientMetadata] = React.useState([]);
  const [selectedClients, setSelectedClients] = React.useState([]);
  const [statusFilter, setStatusFilter] = React.useState('active');
  const [viewMode, setViewMode] = React.useState(() => readStoredString(VIEW_STORAGE_KEY, 'tiles'));
  const [selectedMetrics, setSelectedMetrics] = React.useState(() => readStoredArray(METRICS_STORAGE_KEY, DEFAULT_SELECTED_METRICS));
  const [previewRow, setPreviewRow] = React.useState(null);
  const [page, setPage] = React.useState(1);

  React.useEffect(() => { writeStored(VIEW_STORAGE_KEY, viewMode); }, [viewMode]);
  React.useEffect(() => { writeStored(METRICS_STORAGE_KEY, selectedMetrics); }, [selectedMetrics]);

  React.useEffect(() => {
    let isMounted = true;
    if (!isStaff) {
      setClientMetadata([]);
      setLoadingClientMeta(false);
      return () => { isMounted = false; };
    }
    const loadClients = async () => {
      setLoadingClientMeta(true);
      try {
        const clients = await fetchAllClientMetadata();
        if (!isMounted) return;
        setClientMetadata(clients.map((client = {}) => ({
          id: String(client.id),
          name: client.name || '',
          parentCompanyId: client.parent_company ? String(client.parent_company) : '__none__',
          parentCompanyName: client.parent_company_name || 'No Parent Company',
          hasMapping: Array.isArray(client.mapping_types) && client.mapping_types.length > 0,
        })));
      } catch (err) {
        if (isMounted) { setClientMetadata([]); setError('Unable to load client metadata.'); }
      } finally {
        if (isMounted) setLoadingClientMeta(false);
      }
    };
    loadClients();
    return () => { isMounted = false; };
  }, [isStaff]);

  const clientMetaById = React.useMemo(() => {
    const next = new Map();
    if (isStaff) {
      clientMetadata.forEach((c) => next.set(c.id, c));
    } else {
      clientMemberships.forEach((m) => {
        if (m?.client_id) {
          next.set(String(m.client_id), {
            id: String(m.client_id),
            name: m.client_name || '',
          });
        }
      });
    }
    return next;
  }, [clientMetadata, clientMemberships, isStaff]);

  const clientOptions = React.useMemo(
    () => clientMetadata.map((c) => ({ id: c.id, name: c.name })).sort((a, b) => a.name.localeCompare(b.name)),
    [clientMetadata]
  );

  React.useEffect(() => {
    if (!isStaff) return;
    const allowed = new Set(clientOptions.map((o) => o.id));
    setSelectedClients((prev) => {
      const next = prev.filter((id) => allowed.has(id));
      return arraysEqual(prev, next) ? prev : next;
    });
  }, [clientOptions, isStaff]);

  const effectiveClientIds = React.useMemo(() => {
    if (!isStaff) return [];
    if (selectedClients.length > 0) return selectedClients;
    return clientMetadata.map((c) => c.id);
  }, [clientMetadata, isStaff, selectedClients]);

  React.useEffect(() => {
    let isMounted = true;
    if (loadingClientMeta) return undefined;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const payload = {
          date_after: formatDateToYMD(startDate),
          date_before: formatDateToYMD(endDate),
          ordering: '-total_spend_sum',
          ...(isStaff && effectiveClientIds.length ? { client_ids: effectiveClientIds.join(',') } : {}),
          ...(statusFilter === 'active' ? { active: 'true' } : {}),
        };
        const response = await fetchAdSummaryByAd(payload);
        const data = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.results) ? response.data.results : [];
        if (!isMounted) return;
        const mapped = data.map((row) => mapRow(row, clientMetaById));
        mapped.sort((a, b) => b.spend - a.spend);
        setRows(mapped);
      } catch (err) {
        if (isMounted) { setRows([]); setError('Unable to load creative performance data.'); }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    load();
    return () => { isMounted = false; };
  }, [clientMetaById, effectiveClientIds, endDate, isStaff, loadingClientMeta, startDate, statusFilter]);

  const totals = React.useMemo(() => {
    const base = rows.reduce((acc, row) => {
      acc.spend += row.spend;
      acc.impressions += row.impressions;
      acc.clicks += row.clicks;
      acc.landingPageViews += row.landingPageViews;
      acc.videoPlays3s += row.videoPlays3s;
      acc.thruplays += row.thruplays;
      acc.leads += row.leads;
      acc.booked += row.booked;
      acc.issues += row.issues;
      acc.demos += row.demos;
      acc.netSold += row.netSold;
      acc.netAmount += row.netAmount;
      return acc;
    }, { spend: 0, impressions: 0, clicks: 0, landingPageViews: 0, videoPlays3s: 0, thruplays: 0, leads: 0, booked: 0, issues: 0, demos: 0, netSold: 0, netAmount: 0 });
    return { ...base, ...computeDerivedMetrics(base) };
  }, [rows]);

  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pagedRows = React.useMemo(
    () => rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [rows, safePage]
  );

  // Reset to the first page whenever the result set or layout changes so
  // the user never lands on a now-empty page.
  React.useEffect(() => { setPage(1); }, [rows, viewMode]);

  const handleDateRangeChange = (event) => {
    const selection = event?.selection || {};
    if (selection.startDate) setStartDate(selection.startDate);
    if (selection.endDate) setEndDate(selection.endDate);
  };

  const toggleMetric = (key) => {
    setSelectedMetrics((prev) => {
      const isOn = prev.includes(key);
      if (isOn) {
        if (prev.length === 1) return prev;
        return prev.filter((k) => k !== key);
      }
      return [...prev, key];
    });
  };

  return (
    <Page title="Creative Performance" fullWidth>
      <Stack spacing={3}>
        {error && <Alert severity="error">{error}</Alert>}

        <Card>
          <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
            <Stack spacing={1.5}>
              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', lg: 'center' }}>
                <Box sx={{ minWidth: 280, maxWidth: { lg: 320 } }}>
                  <DateRangePicker startDate={startDate} endDate={endDate} onChange={handleDateRangeChange} style={{ width: '100%' }} />
                </Box>
                {isStaff ? (
                  <Autocomplete
                    multiple
                    size="small"
                    sx={{ minWidth: 220, flex: 1 }}
                    options={clientOptions.map((o) => ({ label: o.name, value: o.id }))}
                    value={clientOptions.filter((o) => selectedClients.includes(o.id)).map((o) => ({ label: o.name, value: o.id }))}
                    onChange={(_, v) => setSelectedClients(v.map((o) => o.value))}
                    isOptionEqualToValue={(o, v) => o.value === v.value}
                    getOptionLabel={(o) => o.label || ''}
                    renderInput={(params) => <TextField {...params} label="Clients" />}
                  />
                ) : null}
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={statusFilter}
                  onChange={(_, v) => { if (v) setStatusFilter(v); }}
                  sx={{ flexShrink: 0 }}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <ToggleButton key={opt.value} value={opt.value}>{opt.label}</ToggleButton>
                  ))}
                </ToggleButtonGroup>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={viewMode}
                  onChange={(_, v) => { if (v) setViewMode(v); }}
                  sx={{ flexShrink: 0 }}
                >
                  <ToggleButton value="tiles" aria-label="Tile view">
                    <Tooltip title="Tile view"><GridViewIcon fontSize="small" /></Tooltip>
                  </ToggleButton>
                  <ToggleButton value="table" aria-label="Table view">
                    <Tooltip title="Table view"><ViewListIcon fontSize="small" /></Tooltip>
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Show metrics:</Typography>
                {METRIC_OPTIONS.map((opt) => {
                  const active = selectedMetrics.includes(opt.key);
                  return (
                    <Chip
                      key={opt.key}
                      label={opt.label}
                      size="small"
                      color={active ? 'primary' : 'default'}
                      variant={active ? 'filled' : 'outlined'}
                      onClick={() => toggleMetric(opt.key)}
                    />
                  );
                })}
              </Stack>
              <Stack direction="row" spacing={2.5} sx={{ flexWrap: 'wrap', rowGap: 1, pt: 0.5 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Spend</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}><DollarFormatter value={totals.spend} /></Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Leads</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{totals.leads.toLocaleString()}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Booked</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{totals.booked.toLocaleString()}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Sold</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{totals.netSold.toLocaleString()}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Revenue</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}><DollarFormatter value={totals.netAmount} /></Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">CoM</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    {totals.netAmount > 0 ? <PercentageFormatter value={totals.costOfMarketing} /> : '—'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Ads</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{rows.length}</Typography>
                </Box>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {loading || loadingClientMeta ? (
          <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
        ) : rows.length === 0 ? (
          <Card><CardContent>
            <Typography variant="body2" color="text.secondary" align="center">No ads in this range.</Typography>
          </CardContent></Card>
        ) : (
          <Stack spacing={2}>
            {viewMode === 'tiles' ? (
              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
                }}
              >
                {pagedRows.map((row) => (
                  <AdTile key={row.uuid} row={row} selectedMetrics={selectedMetrics} onClick={setPreviewRow} />
                ))}
              </Box>
            ) : (
              <Card><CardContent sx={{ p: 0 }}>
                <AdTable rows={pagedRows} selectedMetrics={selectedMetrics} onRowClick={setPreviewRow} />
              </CardContent></Card>
            )}
            {pageCount > 1 ? (
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {`Showing ${(safePage - 1) * PAGE_SIZE + 1}–${Math.min(safePage * PAGE_SIZE, rows.length)} of ${rows.length} ads`}
                </Typography>
                <Pagination
                  count={pageCount}
                  page={safePage}
                  onChange={(_, value) => setPage(value)}
                  size="small"
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Stack>
            ) : null}
          </Stack>
        )}

        <AdPreviewModal row={previewRow} onClose={() => setPreviewRow(null)} />
      </Stack>
    </Page>
  );
}
